import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, HelpCircle, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogIcon,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/v2/design-system/primitives';
import {
  findNextTourStepIndex,
  getTourForRole,
  getTourProgressKey,
  isStaticTourRoute,
  routeTemplateMatches,
  type RoleTourStep,
} from '@/v2/tours/roleTours';
import type { UserPreferences, UserTourProgress } from '@valiosascrm/shared';

const TOUR_EVENT = 'valiosascrm:start-role-tour';
// 42.14: restart pedido FORA do shell — a intenção persiste em sessionStorage e é consumida
// quando o GuidedTour monta. 43.1 (RC-025): a intenção carrega timestamp e só é consumida
// dentro do TTL e com sessão hidratada; expirada é descartada silenciosamente.
const TOUR_RESTART_PENDING_KEY = 'valiosascrm:tour-restart-pending';
const TOUR_RESTART_TTL_MS = 60_000;

function selectorFor(anchor: string): string {
  return `[data-tour="${anchor}"]`;
}

function getAnchor(anchor: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selectorFor(anchor));
}

function devWarn(message: string) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[GuidedTour] ${message}`);
  }
}

export function startRoleTour() {
  try {
    window.sessionStorage.setItem(TOUR_RESTART_PENDING_KEY, String(Date.now()));
  } catch {
    /* sessionStorage indisponível — o evento abaixo cobre o caso in-shell */
  }
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

export function GuidedTour() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRetry, setAnchorRetry] = useState(0);
  // 42.11 (RC-025): auto-open é CONVITE — navegação/âncora só depois de consentimento
  // explícito (clique no primário) ou restart manual. Sem isso o tour sequestrava
  // deep-link de usuária nova (navigate para a rota do passo em cima da intenção dela).
  const [started, setStarted] = useState(false);

  const role = user?.role;
  const tour = useMemo(() => (role ? getTourForRole(role) : null), [role]);
  const progressKey = useMemo(
    () => (role && tour ? getTourProgressKey(role, tour.id) : null),
    [role, tour],
  );

  const savedProgress = progressKey ? user?.preferences?.tourProgress?.[progressKey] : undefined;
  const currentStep: RoleTourStep | undefined = tour?.steps[stepIndex];

  const saveProgress = useCallback(
    async (progress: UserTourProgress) => {
      if (!user || !progressKey) return;

      const currentPreferences = (user.preferences ?? {}) as UserPreferences;
      const tourProgress = {
        ...(currentPreferences.tourProgress ?? {}),
        [progressKey]: progress,
      };
      const preferences = { ...currentPreferences, tourProgress };
      setUser({ ...user, preferences });

      try {
        await api.patch('/auth/me', { preferences: { tourProgress } });
      } catch {
        devWarn(`Failed to persist tour progress for ${progressKey}`);
      }
    },
    [progressKey, setUser, user],
  );

  useEffect(() => {
    if (!tour || !progressKey) return;
    if (savedProgress?.completedAt || savedProgress?.dismissedAt) return;

    setStepIndex(savedProgress?.stepIndex ?? 0);
    setOpen(true);
  }, [progressKey, savedProgress?.completedAt, savedProgress?.dismissedAt, savedProgress?.stepIndex, tour]);

  useEffect(() => {
    const restart = () => {
      try {
        window.sessionStorage.removeItem(TOUR_RESTART_PENDING_KEY);
      } catch {
        /* noop */
      }
      setStepIndex(0);
      // Restart manual = consentimento: pode navegar e ignora dismissedAt salvo.
      setStarted(true);
      setOpen(true);
    };
    window.addEventListener(TOUR_EVENT, restart);
    return () => window.removeEventListener(TOUR_EVENT, restart);
  }, []);

  // 43.1 (RC-025): consumo da intenção persistida SÓ com sessão hidratada (Gap B) e dentro
  // do TTL de 60 s (Gaps C/D) — intenção expirada é removida sem abrir o tour.
  const hasSession = Boolean(user);
  useEffect(() => {
    if (!hasSession) return;
    let raw: string | null = null;
    try {
      raw = window.sessionStorage.getItem(TOUR_RESTART_PENDING_KEY);
      if (raw !== null) window.sessionStorage.removeItem(TOUR_RESTART_PENDING_KEY);
    } catch {
      return;
    }
    if (raw === null) return;
    const requestedAt = Number(raw);
    if (!Number.isFinite(requestedAt) || Date.now() - requestedAt > TOUR_RESTART_TTL_MS) return;
    setStepIndex(0);
    setStarted(true);
    setOpen(true);
  }, [hasSession]);

  // 42.11 (RC-025): dismiss/complete que chega DEPOIS do mount (refetch de /auth/me →
  // setUser, ex.: dispensado em outra aba/device) fecha o convite aberto. Não fecha
  // tour started — quem está ativamente no tour aqui tem prioridade sobre o refetch.
  useEffect(() => {
    if (!open || started) return;
    if (savedProgress?.dismissedAt || savedProgress?.completedAt) {
      setOpen(false);
    }
  }, [open, started, savedProgress?.dismissedAt, savedProgress?.completedAt]);

  useEffect(() => {
    // 42.11 (RC-025): em modo convite (started=false) NADA de navigate/âncora —
    // o dialog apresenta o tour na tela em que a usuária está.
    if (!open || !started || !tour || !role) return;

    const nextIndex = findNextTourStepIndex({
      role,
      tour,
      fromIndex: stepIndex,
      pathname: location.pathname,
      // Route changes and lazy pages can render the tour before the target anchor
      // exists. The component below waits briefly for the anchor before skipping.
      anchorExists: () => true,
      warn: devWarn,
    });

    if (nextIndex === null) {
      setOpen(false);
      void saveProgress({ stepIndex: tour.steps.length, completedAt: new Date().toISOString() });
      return;
    }

    if (nextIndex !== stepIndex) {
      setStepIndex(nextIndex);
      setAnchorRetry(0);
      return;
    }

    const step = tour.steps[nextIndex];
    if (isStaticTourRoute(step.route) && !routeTemplateMatches(step.route, location.pathname)) {
      setAnchorRetry(0);
      navigate(step.route);
      return;
    }

    const target = getAnchor(step.anchor);
    if (!target) {
      if (anchorRetry < 10) {
        const retry = window.setTimeout(() => setAnchorRetry((value) => value + 1), 150);
        return () => window.clearTimeout(retry);
      }
      devWarn(`Tour step skipped because anchor is missing: ${step.anchor}`);
      setStepIndex((index) => index + 1);
      setAnchorRetry(0);
      return;
    }
    setAnchorRetry(0);
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    target.classList.add('v2-tour-highlight');
    return () => target.classList.remove('v2-tour-highlight');
  }, [anchorRetry, location.pathname, navigate, open, role, saveProgress, started, stepIndex, tour]);

  const closeTour = async () => {
    setOpen(false);
    await saveProgress({ stepIndex, dismissedAt: new Date().toISOString() });
  };

  const completeTour = async () => {
    setOpen(false);
    await saveProgress({ stepIndex: tour?.steps.length ?? stepIndex, completedAt: new Date().toISOString() });
  };

  const goNext = async () => {
    if (!tour) return;
    const next = stepIndex + 1;
    if (next >= tour.steps.length) {
      await completeTour();
      return;
    }
    setStepIndex(next);
    setAnchorRetry(0);
    await saveProgress({ stepIndex: next });
  };

  if (!tour || !currentStep) return null;

  const isLastStep = stepIndex >= tour.steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? void closeTour() : setOpen(true))}>
      <DialogContent size="sm" className="top-auto bottom-5 left-auto right-5 translate-x-0 translate-y-0">
        <DialogHeader>
          <DialogIcon tone="primary">
            <HelpCircle />
          </DialogIcon>
          <DialogTitle>{currentStep.title}</DialogTitle>
          <DialogDescription>
            Passo {Math.min(stepIndex + 1, tour.steps.length)} de {tour.steps.length} · {tour.goal}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-so-14 leading-relaxed text-sober-secondary">{currentStep.body}</p>
        </DialogBody>
        <DialogFooter className="flex-wrap justify-between">
          <Button variant="ghost" onClick={closeTour}>
            <X /> Pular
          </Button>
          <div className="flex gap-2">
            {started && (
              <Button
                variant="secondary"
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                disabled={stepIndex === 0}
              >
                <ArrowLeft /> Voltar
              </Button>
            )}
            {started ? (
              <Button onClick={goNext}>
                {isLastStep ? (
                  <>
                    <Check /> Concluir
                  </>
                ) : (
                  <>
                    Próximo <ArrowRight />
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={() => setStarted(true)}>
                {stepIndex > 0 ? 'Continuar tour' : 'Começar tour'} <ArrowRight />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
